import React, { useState, useEffect } from "react";
import MPActionBar from "./MProjectActionBar/ActionBar";
import DynamicTable from "../../../components/Table/MTable";
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";
import axios from "axios";
import { useSelector } from "react-redux";

const MProjects = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { role } = useSelector((state) => state.auth);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const apiEndpoint = `${API_BASE_URL}/mproject/mproject`;

  const columns = [
    { key: "ProjectID", label: "Project ID" },
    { key: "ProjectName", label: "Project Name" },
    { key: "LocationName", label: "Location" },
    { key: "ProjectIncharge", label: "Project Incharge" },
    { key: "StageName", label: "Stage" },
    { key: "MailID", label: "Mail ID" }
  ];

  // Fetch projects with proper state management
  const fetchProjects = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(apiEndpoint);
      
      // Ensure we have unique records based on ProjectID
      const uniqueProjects = Array.from(
        new Map(response.data.map(item => [item.ProjectID, item])).values()
      );
      
      setProjects(uniqueProjects);
      setFilteredProjects(uniqueProjects);
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle search without causing duplicates
  const handleSearch = (query) => {
    const searchQuery = query.toLowerCase().trim();
    
    if (!searchQuery) {
      setFilteredProjects(projects);
    } else {
      const filtered = projects.filter((project) =>
        project.ProjectName?.toLowerCase().includes(searchQuery) ||
        project.LocationName?.toLowerCase().includes(searchQuery) ||
        project.ProjectID?.toString().includes(searchQuery)
      );
      setFilteredProjects(filtered);
    }
  };

  // Prevent unnecessary re-renders
  const handleOperationSuccess = React.useCallback(() => {
    fetchProjects();
    setSelectedRow(null);
  }, []);

  // Use proper dependency array
  useEffect(() => {
    fetchProjects();
    // Clean up function to prevent memory leaks
    return () => {
      setProjects([]);
      setFilteredProjects([]);
    };
  }, []); // Empty dependency array as we only want to fetch once on mount

  return (
    <div className="min-h-screen flex flex-col">
      <Header exportEndpoint={apiEndpoint} />

      <div className="flex-grow p-4">
        <MPActionBar
          title="Project"
          rowKey="ProjectID"
          deleteMessage="Are you sure you want to remove this project permanently?"
          fields={[
            { name: "ProjectName", label: "Project Name", required: true },
            { name: "LocationID", label: "Location", type: "select", options: "locations" },
            { name: "ProjectIncharge", label: "Project Incharge", type: "select", options: "users" },
            { name: "StageID", label: "Stage", type: "select", options: "stages" },
            { name: "MailID", label: "Mail ID", type: "email" },
          ]}
          apiEndpoint={apiEndpoint}
          data={filteredProjects}
          selectedRow={selectedRow}
          onRowSelect={setSelectedRow}
          onRefresh={handleOperationSuccess}
          onSearch={handleSearch}
          userRole={role}
        />
        
        <div className="mt-6 mb-2 text-center">
          <h3 className="text-xl font-semibold">Project Table</h3>
        </div>

        <div className="mt-4">
          {isLoading ? (
            <div className="text-center py-4">Loading projects...</div>
          ) : (
            <DynamicTable
              columns={columns}
              data={filteredProjects}
              selectedRow={selectedRow}
              onRowSelect={setSelectedRow}
              rowKey="ProjectID"
              highlightColor="bg-blue-100"
              noDataMessage="No projects found."
            />
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MProjects;